import { MouseEvent, useState } from 'react'
import { Menu, Box, PopoverOrigin, MenuItem } from '@mui/material'
import { ExpandMore, Check, ExpandLess } from '@mui/icons-material'
import theme from 'lib/theme'
import { IconButtonWrapper } from 'components/IconDropdown/IconDropdown.styled'
import { useFormikContext } from 'formik'

interface SmallDropdownProps {
  anchorOrigin?: PopoverOrigin
  transformOrigin?: PopoverOrigin
  options: string[]
  ariaLabel: string
  fieldName: string
  selectedOption: string
  handleChange?: (option: string) => void
}

export const SmallDropdown = ({
  anchorOrigin,
  transformOrigin,
  options,
  fieldName,
  ariaLabel,
  selectedOption,
  handleChange,
}: SmallDropdownProps) => {
  const { setFieldValue } = useFormikContext()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleMenuItemClick = (option: string) => {
    if (handleChange) {
      handleChange(option)
    } else {
      setFieldValue(fieldName, option.toLowerCase())
    }
    setAnchorEl(null)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <div>
      <IconButtonWrapper
        sx={{
          backgroundColor: theme.palette.secondary.main,
          '&:hover': {
            background: theme.palette.action.hover,
          },
          borderRadius: theme.spacing(0.5),
          padding: 0,
          marginLeft: theme.spacing(0.5),
          width: '44px',
        }}
        isIconOnly
        isOpen={open}
        aria-label={ariaLabel}
        id='long-button'
        aria-controls={open ? 'long-menu' : undefined}
        aria-expanded={open}
        aria-haspopup='true'
        onClick={handleClick}>
        <Box
          sx={{
            color: theme.palette.text.secondary,
            fontWeight: 'bold',
            fontSize: theme.typography.body1.fontSize,
            display: 'flex',
            justifyContent: 'space-between',
            paddingTop: theme.spacing(1),
            paddingBottom: theme.spacing(1),
            paddingLeft: theme.spacing(0.5),
          }}>
          {selectedOption.toUpperCase()}
        </Box>
        {anchorEl ? (
          <ExpandLess fontSize='small' />
        ) : (
          <ExpandMore fontSize='small' />
        )}
      </IconButtonWrapper>
      <Menu
        id='long-menu'
        MenuListProps={{
          'aria-labelledby': 'long-button',
        }}
        transformOrigin={
          transformOrigin || { vertical: 'top', horizontal: 'left' }
        }
        anchorOrigin={
          anchorOrigin || { vertical: 'bottom', horizontal: 'left' }
        }
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            borderRadius: '1px',
            width: '44px',
          },
        }}
        sx={{ ul: { padding: 0 } }}>
        {options.map((option) => (
          <MenuItem
            data-testid={`${fieldName}-${option}`}
            sx={{
              fontSize: theme.typography.body1.fontSize,
              display: 'flex',
              '&:hover': {
                background: theme.palette.secondary.main,
              },
              '&:focus': {
                background: 'transparent',
              },
              justifyContent: 'space-between',
              padding: '5px',
            }}
            key={option}
            value={option}
            selected={option.toLowerCase() === selectedOption.toLowerCase()}
            onClick={() => handleMenuItemClick(option)}>
            {option.toUpperCase()}
            {option.toLowerCase() === selectedOption.toLocaleLowerCase() && (
              <Check
                sx={{
                  fill: theme.palette.primary.main,
                  width: theme.spacing(2),
                }}
              />
            )}
          </MenuItem>
        ))}
      </Menu>
    </div>
  )
}
